import * as DynamakerContract from '../../integration/DynamakerContract'
import { DivergentPropertiesPerItemGroup } from './itemsSlice'
import { getDmPropsForChosenValues } from '../../helpers/getDmPropsForChosenValues'
import divergentPropertiesIgnorelistJson from '../../data/config/divergentPropertiesIgnorelist.json'
import lfcDimensionsVisibilityJson from '../../data/config/lfcDimensionsVisibility.json'

const hiddenDimensionCodes = new Set(
  lfcDimensionsVisibilityJson.filter((entry) => entry.visibility === 'HIDDEN').map((entry) => entry.dimensionCode)
)

interface LfcItemForLookup {
  configuratorContextId?: string
  dimensions: any[]
}

// TODO mappa upp detta i en konfig-fil som används överallt istället
const getItemGroup = (item: string): DynamakerContract.ItemGroup => {
  switch (item) {
    case 'door':
      return 'doors'
    case 'window':
      return 'windows'
    case 'garage-door':
      return 'garageDoors'
    case 'double-door':
      return 'doubleDoors'
    case 'sliding-door':
      return 'slidingDoors'
    case 'facade-section':
      return 'facadeSections'
    default:
      throw new Error(`Case for ItemType ${item} not defined in getNextDivergentProperties()`)
  }
}

const createEmptyResult = (): DivergentPropertiesPerItemGroup => ({
  doors: {},
  doubleDoors: {},
  facadeSections: {},
  garageDoors: {},
  slidingDoors: {},
  windows: {},
})

export const getNextDivergentProperties = (
  bom: DynamakerContract.ConfigResult['bom'],
  renderItems: Record<DynamakerContract.ItemGroup, any[]>,
  lfcItems: LfcItemForLookup[]
): DivergentPropertiesPerItemGroup => {
  // Extract all item articles from ITEMS sections in one pass
  const itemArticles: Array<{ item: string; erpItemNumber: string; configData?: any }> = bom.flatMap(
    (section: DynamakerContract.ConfigurationSection) =>
      section.type === 'ITEMS'
        ? section.articles.flatMap((art: any) => (DynamakerContract.itemTypes.includes(art.item) ? [art] : []))
        : []
  )

  const result = createEmptyResult()

  // --- Filter property divergence (from renderItems) ---

  // Occuring erpItemNumbers (excluding duplicates)
  const occuringNumbers = [
    ...new Set(itemArticles.map((art) => art.erpItemNumber)),
  ] as DynamakerContract.ArticleQty['erpItemNumber'][]

  // All render items matching occuring erpItemNumbers (excluding duplicates considering erpItemNumber of renderItem)
  const matchingRenderItems: any[] = Object.values(renderItems ?? {})
    .flat()
    .filter((item) => occuringNumbers.some((occuringNumber) => occuringNumber === item.erpItemNumber))
    .filter((item, index, arr) => arr.findIndex((item_) => item_.erpItemNumber === item.erpItemNumber) === index)

  matchingRenderItems.forEach((renderItem, _, arr) => {
    const divergentPropertiesArr: [string, string][] = Object.entries(renderItem?._filterProperties ?? {}).filter(
      ([property, value]) =>
        arr.some((renderItem_) =>
          Object.entries(renderItem_._filterProperties ?? {}).some(
            ([property_, value_]) =>
              property_ === property && value_ !== value && !divergentPropertiesIgnorelistJson.includes(property_)
          )
        )
    ) as [string, string][]

    if (divergentPropertiesArr.length) {
      const itemGroup = getItemGroup(renderItem.item)

      result[itemGroup] = {
        ...result[itemGroup],
        ...Object.fromEntries(
          divergentPropertiesArr.map(([property, value]) => [
            property,
            {
              origin: 'filterProperties' as const,
              values: {
                ...result[itemGroup][property]?.values,
                [value]: [...(result[itemGroup][property]?.values?.[value] ?? []), renderItem.erpItemNumber],
              },
            },
          ])
        ),
      }
    }
  })

  // --- DM value divergence (from lfcItems) ---

  // Group LFC articles (those with configData) by item group
  const lfcArticlesByGroup: Record<string, Array<{ erpItemNumber: string; configData: any }>> = {}
  for (const article of itemArticles) {
    if (!article.configData) continue
    const group = getItemGroup(article.item)
    if (!lfcArticlesByGroup[group]) lfcArticlesByGroup[group] = []
    lfcArticlesByGroup[group].push(article as { erpItemNumber: string; configData: any })
  }

  for (const [itemGroup, articles] of Object.entries(lfcArticlesByGroup)) {
    if (articles.length < 2) continue

    // Resolve dmProps for each article
    const resolvedArticles = articles.map((article) => {
      const lfcItem = lfcItems.find((lfc) => lfc.configuratorContextId === article.configData.contextId)

      if (!lfcItem) {
        console.warn(`getNextDivergentProperties: No lfcItem found for contextId ${article.configData.contextId}`)
        return {
          erpItemNumber: article.erpItemNumber,
          dmProps: {} as Record<string, any>,
        }
      }

      const dmProps = getDmPropsForChosenValues(article.configData.chosenValues, lfcItem.dimensions)

      return { erpItemNumber: article.erpItemNumber, dmProps }
    })

    // Find all dynamakerCodes present across articles, excluding hidden dimensions and ignorelisted codes
    const allCodes = new Set<string>()
    for (const { dmProps } of resolvedArticles) {
      for (const code of Object.keys(dmProps)) {
        if (!divergentPropertiesIgnorelistJson.includes(code)) {
          allCodes.add(code)
        }
      }
    }

    // Remove codes that only map to hidden dimensions
    for (const code of [...allCodes]) {
      const allHidden = articles.every((article) => {
        const lfcItem = lfcItems.find((lfc) => lfc.configuratorContextId === article.configData.contextId)
        const dim = lfcItem?.dimensions?.find((d: any) => d.dynamakerCode === code)
        return dim?.dimensionCode && hiddenDimensionCodes.has(dim.dimensionCode)
      })
      if (allHidden) allCodes.delete(code)
    }

    // Check each code for divergence
    for (const code of allCodes) {
      const valueToErpNumbers: Record<string, string[]> = {}

      for (const { erpItemNumber, dmProps } of resolvedArticles) {
        const value = dmProps[code]
        if (value === undefined) continue
        const valueKey = String(value)
        if (!valueToErpNumbers[valueKey]) valueToErpNumbers[valueKey] = []
        if (!valueToErpNumbers[valueKey].includes(erpItemNumber)) {
          valueToErpNumbers[valueKey].push(erpItemNumber)
        }
      }

      // Divergent if more than one distinct value exists
      if (Object.keys(valueToErpNumbers).length > 1) {
        result[itemGroup as DynamakerContract.ItemGroup][code] = {
          origin: 'dmProps',
          values: valueToErpNumbers,
        }
      }
    }
  }

  return result
}
